/**
 * @module sortMediaItem
 */

/**
 * Convenience function to sort the specified media items by date.
 *
 * @param {Array} mediaItems - Array of media items to sort.
 *
 * @returns {Array} Array of sorted media items.
 */
function sortDateBased(mediaItems) {
  return mediaItems.sort((a, b) => {
    const dateA = new Date(a.startDate);
    const dateB = new Date(b.startDate);
    return dateB - dateA;
  });
}

/**
 * Convenience function to sort the specified media items by episode number.
 *
 * @param {Array} mediaItems - Array of media items to sort.
 *
 * @returns {Array} Array of sorted media items.
 */
function sortEpisodeBased(mediaItems) {
  return mediaItems.sort(
    (a, b) => b.media_item_type.episodeNumber - a.media_item_type.episodeNumber,
  );
}

/**
 * Convenience function to sort the specified media items based on the collection type.
 *
 * @param {Array} mediaItems - Array of media items to sort.
 * @param {string} collectionType - Collection type of the media items (e.g. 'story').
 *
 * @returns {Array} Array of sorted media items.
 */
function sortMedia(mediaItems, collectionType) {
  if (collectionType === 'story') {
    return sortEpisodeBased(mediaItems);
  }
  return sortDateBased(mediaItems);
}

/**
 * Convenience function to return the uuid value of the specified media type.
 *
 * @param {string} type - The media type value (e.g. 'stories').
 *
 * @returns {string} The uuid value of the specified media type, or undefined if no match found.
 */
function uuidFromMediaType(type) {
  const mediaTypeDictionary = {
    crosstown: 'a288696f-4a12-4242-bb97-92691fdfad11',
    'early-childhood': '6de53ff6-2a75-4ccd-91fc-efbc46957447',
    konnect: '607ed7b2-fea6-4d90-8421-5eef817107b4',
    leadership_podcast: '1ffd096f-fed1-43bc-a032-c9323cbd112e',
    lifegroups: '798d4b5d-c09d-4a9f-95d6-e0e2ec46924f',
    loop: '307e5e41-0af6-4b7d-9c12-cabc2d6b7033',
    stories: 'c6e0146d-a44f-4595-bef2-342a3e13c2c3',
    switch: 'e65815b1-7a87-4db0-8a77-f32c932acfdc',
    weekend_message: '81c51bae-73d7-41eb-83b7-f2e6318e5606',
    worship: 'b4311ebf-2071-4f48-bc7d-143bc85553ca',
  };

  return mediaTypeDictionary[type];
}

/**
 * Convenience function to return the media type from the specified uuid value.
 *
 * @param {string} uuid - The uuid value (e.g. 'c6e0146d-a44f-4595-bef2-342a3e13c2c3').
 *
 * @returns {string} The media type value of the specified uuid, or undefined if no match found.
 */
function mediaTypeFromUuid(uuid) {
  const uuidDictionary = {
    '1ffd096f-fed1-43bc-a032-c9323cbd112e': 'leadership_podcast',
    '6de53ff6-2a75-4ccd-91fc-efbc46957447': 'early-childhood',
    '81c51bae-73d7-41eb-83b7-f2e6318e5606': 'weekend_message',
    '307e5e41-0af6-4b7d-9c12-cabc2d6b7033': 'loop',
    '607ed7b2-fea6-4d90-8421-5eef817107b4': 'konnect',
    '798d4b5d-c09d-4a9f-95d6-e0e2ec46924f': 'lifegroups',
    'a288696f-4a12-4242-bb97-92691fdfad11': 'crosstown',
    'b4311ebf-2071-4f48-bc7d-143bc85553ca': 'worship',
    'c6e0146d-a44f-4595-bef2-342a3e13c2c3': 'stories',
    'e65815b1-7a87-4db0-8a77-f32c932acfdc': 'switch',
  };

  return uuidDictionary[uuid];
}

module.exports = {
  mediaTypeFromUuid,
  sortDateBased,
  sortEpisodeBased,
  sortMedia,
  uuidFromMediaType,
};
